import { useEffect, useState } from 'react'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import { getEngineeringTableRows, deleteEngineeringTableRow } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Engineering Table',
    isCurrentPage: true
  }
]

function inchesToFtIn (totalInches) {
  if (!totalInches || totalInches <= 0) return '—'
  const ft = Math.floor(totalInches / 12)
  const inches = totalInches % 12
  return `${ft}'-${inches}"`
}

export const EngineeringTableList = () => {
  const navigate = useNavigate()
  const disclosure = useDisclosure()
  const { createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getEngineeringTableRows()
      setRows(response.rows || [])
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const onRemove = (row) => {
    setSelectedRow(row)
    disclosure.onOpen()
  }

  const handleRemove = async () => {
    setIsRemoving(true)
    try {
      await deleteEngineeringTableRow(selectedRow?.id)
      setIsRemoving(false)
      disclosure.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response?.data)
      setIsRemoving(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Vertical Support Engineering Table</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/system/vertical-support-engineering/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add Row
          </Button>
        </Flex>
        <Box overflowX="auto">
          <Box as="table" w="100%" fontSize="sm" borderWidth={1}>
            <Box as="thead" bg="gray.100">
              <Box as="tr">
                <Box as="th" p={2} textAlign="left">OAH (ft)</Box>
                <Box as="th" p={2} textAlign="left">OAW (ft)</Box>
                <Box as="th" p={2} textAlign="left">Tube Size</Box>
                <Box as="th" p={2} textAlign="left">Dual Tube Size</Box>
                <Box as="th" p={2} textAlign="left">Footer Depth</Box>
                <Box as="th" p={2} textAlign="left">Source</Box>
                <Box as="th" p={2} textAlign="left">Actions</Box>
              </Box>
            </Box>
            <Box as="tbody">
              {isLoading && (
                <Box as="tr"><Box as="td" colSpan={7} p={4} textAlign="center">Loading...</Box></Box>
              )}
              {!isLoading && rows.length === 0 && (
                <Box as="tr"><Box as="td" colSpan={7} p={4} textAlign="center">No rows found.</Box></Box>
              )}
              {!isLoading && rows.map(row => (
                <Box as="tr" key={row.id} _hover={{ bg: 'gray.50' }}>
                  <Box as="td" p={2}>{row.oahFt}</Box>
                  <Box as="td" p={2}>{row.oawFt}</Box>
                  <Box as="td" p={2}>{row.tubeSize}</Box>
                  <Box as="td" p={2}>{row.dualTubeSize}</Box>
                  <Box as="td" p={2}>{inchesToFtIn(row.footerDepthIn)} ({row.footerDepthIn}&quot;)</Box>
                  <Box as="td" p={2}>{row.source || '—'}</Box>
                  <Box as="td" p={2}>
                    <Flex columnGap={2}>
                      <Button as={NavLink} to={`/system/vertical-support-engineering/edit/${row.id}`} size="xs" colorScheme="blue">Edit</Button>
                      <Button size="xs" colorScheme="red" onClick={() => onRemove(row)}>Remove</Button>
                    </Flex>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Text mt={2} fontSize="sm" color="gray.500">{rows.length} rows total</Text>
      </Box>
      <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove Row</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure to remove OAH={selectedRow?.oahFt}, OAW={selectedRow?.oawFt}?</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleRemove} isLoading={isRemoving}>Remove</Button>
            <Button variant="ghost" onClick={disclosure.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
